/**
 * createApiClient - Configurable Axios client factory
 *
 * Creates an Axios instance with:
 * - Request interceptor for Bearer token attachment + ABG-001 pre-flight
 * - Response interceptor for 401 token refresh + automatic request retry
 * - Standardized error normalization
 *
 * @module @puffin-core/api-client/createApiClient
 */

import axios from 'axios';
import { CLIENT_KIND } from '@puffin-core/client-contract';
import { clientContractHeaders } from './clientContractHeaders.js';
import { realtimeOriginHeaders } from './realtimeOrigin.js';
import { NoAuthTokenError } from './errors/NoAuthTokenError.js';
import { isSessionUnreachable } from './errors/SessionUnreachableError.js';
import {
  BackgroundRequestPausedError,
  BACKGROUND_PAUSE_REASONS,
} from './errors/BackgroundRequestPausedError.js';
import { getPageActivityMonitor } from './userActivity.js';
import { getPageStepUpSeam, retryAfterStepUp } from './stepUpSeam.js';
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  retryWhileIdempotencyInProgress,
} from './idempotencyInProgressRetry.js';
import { DEFAULT_PUBLIC_PATHS, isPublicPath } from './publicPaths.js';
import { unwrapEnvelope } from './envelope.js';
import { assertNotHtmlResponse } from './htmlResponseGuard.js';
import { injectTraceContext } from '@puffin-core/tracing-browser';

/**
 * The verbs that WRITE, and so can cause a realtime emit the writer's own session would otherwise re-read on
 * (APPROVALS-STORE-ROOMS-001 U7). axios lower-cases `method` before any interceptor runs.
 */

/**
 * Normalized error shape returned by the response interceptor.
 *
 * `code` and `errorCode` are DIFFERENT things and both are load-bearing:
 *   - `code` is the AXIOS TRANSPORT code (`ERR_NETWORK`, `ERR_CANCELED`).
 *     ~262 sites across the web apps read it, e.g. port-web's compliance hooks
 *     do `err.code === 'ERR_CANCELED'`. Its meaning must never change.
 *   - `errorCode` is the SERVER's machine-readable business code from the
 *     RES-ENV-001 envelope (`PERIOD_OVERLAP`, `BUDGET_ALREADY_EXISTS`).
 *
 * @typedef {Object} NormalizedError
 * @property {number} status - HTTP status code (0 for network errors)
 * @property {string} message - Human-readable message; the server's sentence when it sent one
 * @property {string|null} code - Axios transport code (e.g. 'ERR_NETWORK')
 * @property {string|null} errorCode - Server business code (SCREAMING_SNAKE), or null
 * @property {*|null} errorDetails - Server `error.details`, or null
 * @property {Object|null} data - Full response body or null
 */

/**
 * Normalize an Axios error into a consistent shape.
 *
 * RES-ENV-001: `message` now reads the server's `error.message` off the
 * canonical envelope. It previously read a top-level `data.message` — a field
 * NOTHING in the ecosystem ever wrote — so every error in every app fell back
 * to axios's generic "Request failed with status code NNN". A UAT tester once
 * reported a bug verbatim as "409 error", because that was all the UI could show.
 *
 * A non-canonical body (network failure, proxy HTML, opaque gateway JSON)
 * degrades to `errorCode: null` and axios's own message.
 *
 * @param {import('axios').AxiosError} error - Axios error object
 * @returns {NormalizedError}
 */
function normalizeError(error) {
  const body = error.response?.data;
  const { message, errorCode, errorDetails } = unwrapEnvelope(body);

  return {
    status: error.response?.status || 0,
    message: message || error.message || 'Network error',
    code: error.code || null,
    errorCode,
    errorDetails,
    data: body || null,
  };
}

/**
 * Detect an abort / cancellation error from any of the shapes axios +
 * native fetch emit. Used by the response interceptor so all aborted
 * requests surface as a single standard `AbortError` DOMException,
 * regardless of whether they were cancelled by axios internal cancel,
 * a user-supplied AbortController, or an axios request timeout.
 *
 * Called only from the response-rejection branch, which axios guarantees
 * to invoke with an Error-shaped object — no null/undefined check needed.
 *
 * @param {Error} error
 * @returns {boolean}
 */
function isAbortLikeError(error) {
  return (
    error.name === 'AbortError' ||
    error.name === 'CanceledError' ||
    error.code === 'ERR_CANCELED' ||
    error.code === 'ECONNABORTED'
  );
}

/**
 * True when a request body is a browser FormData multipart payload.
 *
 * The instance default forces `Content-Type: application/json`. For a multipart
 * body that header strips the boundary axios would otherwise derive, so the
 * server can't parse the upload (multer sees no file → 422). The request
 * interceptor uses this to drop the forced header and let axios re-derive
 * `multipart/form-data; boundary=…`. Guarded so a runtime with no global
 * FormData never throws on the instanceof check.
 *
 * @param {*} data - The request body (`reqConfig.data`).
 * @returns {boolean}
 */
function isFormDataBody(data) {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

/**
 * Drop a forced `Content-Type` from a request's headers so axios re-derives the
 * multipart boundary from a FormData body. Handles both an AxiosHeaders
 * instance (v1 — has `.delete()`) and a plain-object headers bag (tests).
 *
 * @param {Object} headers - `reqConfig.headers`.
 * @returns {void}
 */
function stripForcedContentType(headers) {
  if (!headers) {
    return;
  }
  if (typeof headers.delete === 'function') {
    headers.delete('Content-Type');
    return;
  }
  delete headers['Content-Type'];
  delete headers['content-type'];
}

/**
 * Create a configured Axios instance.
 *
 * @param {Object} config
 * @param {string} config.baseURL - API base URL (e.g., '/api/v1/payday')
 * @param {Function} [config.getToken] - Returns current auth token string (or null).
 *   When provided, an ABG-001 pre-flight runs on every request: if no token is
 *   available AND the request URL is not in `publicPaths`, the request is
 *   rejected locally with `NoAuthTokenError` (no network round-trip). Per-request
 *   overrides via `{ authRequired: false | true }` opt out of / into the pre-flight.
 * @param {Function} [config.onTokenRefresh] - Async function that attempts token refresh. Resolves the
 *   new access token string on success, or null/undefined when the server REFUSED the refresh; rejects
 *   with `SessionUnreachableError` when the server could not be reached (U15, owner ruling Q6: the
 *   session is kept and the request rejects as unreachable, status 0).
 * @param {Function} [config.onRefreshFailed] - Called when the server refused the 401's refresh. Use
 *   for clearing tokens and redirecting to login. Never called for an unreachable server.
 * @param {ReadonlyArray<string>} [config.publicPaths] - Allow-list of URL paths, RELATIVE to `baseURL` and
 *   spelled exactly as this client's callers request them, that bypass the pre-flight — the
 *   requests the client makes with NO session. Defaults to `DEFAULT_PUBLIC_PATHS` (`/health`
 *   only). A caller-supplied list REPLACES the default; state the whole list at the construction
 *   site (see `publicPaths.js` for why a shared list cannot fit every base).
 * @param {number} [config.timeout=30000] - Request timeout in ms
 * @param {boolean} [config.withCredentials=false] - Send cookies cross-origin
 * @param {string} [config.clientKind='web'] - Which client this is (`web` or `native`, `@puffin-core/client-contract`):
 *   every request declares it with its contract level, and a web page also its build (WEB-APP-VERSION-REFRESH-001).
 * @param {Object} [config.activityMonitor] - The user-activity record background requests are judged
 *   against (`createUserActivityMonitor`); defaults to the page's (`getPageActivityMonitor`). Used
 *   only by a SESSION client — one with `onTokenRefresh`.
 * @param {{isArmed: Function, request: Function}} [config.stepUpSeam] - Where a second-factor refusal
 *   asks for the prompt (`createStepUpSeam`); defaults to the page's (`getPageStepUpSeam`). Used only
 *   by an AUTHENTICATED client — one with `getToken`; a public client has no person to ask.
 * @returns {import('axios').AxiosInstance}
 *
 * ## A same-key request the server is still running waits (finish-gate unit M)
 *
 * A `409 IDEMPOTENCY_IN_PROGRESS` (the server guard's answer to a second request carrying a key whose
 * first request is still in its handler) waits the server's `Retry-After` (at most 5 s) and re-sends the
 * SAME request — the same key, the same body — as many times as fit the request's own timeout; only
 * then does the caller see the 409. See `idempotencyInProgressRetry.js`.
 *
 * ## A step-up-gated request prompts by construction (PLN-DEPT-TAB-005)
 *
 * A `403 MFA_REQUIRED` (the server's `requireMfaForAction`) asks the page's step-up seam — the app's
 * MFA dialog, registered while it is mounted — for the action the server named, and re-sends the
 * request ONCE with the re-minted token when the code is accepted. A dismissed dialog, an unarmed
 * seam or a retry the server still refuses rejects with the server's refusal, unchanged, so the
 * caller's ordinary error path runs and its form is untouched. See `stepUpSeam.js`.
 *
 * ## Background requests — `{ background: true }` (owner ruling D-1, D2-Q1)
 *
 * A request a poller makes on its own schedule — never on a user's action — is marked
 * `api.get(url, { background: true })`. A background request never keeps a session alive: when it
 * 401s it refreshes ONLY if the user has interacted with the page since the presented access token
 * was first used. Otherwise it rejects with `BackgroundRequestPausedError` (no refresh, no
 * `onRefreshFailed` — the session is not over, nobody is here), and every later background request
 * presenting the same token is declined locally until the token changes (a user-initiated request
 * refreshed it) or the user interacts again. An unattended tab therefore stops refreshing when its
 * access token lapses, and the server's 30-minute idle window ends the session; the user's next
 * click finds it ended and signs in again.
 */
export function createApiClient(config) {
  const {
    baseURL,
    getToken,
    onTokenRefresh,
    onRefreshFailed,
    publicPaths = DEFAULT_PUBLIC_PATHS,
    timeout = DEFAULT_REQUEST_TIMEOUT_MS,
    withCredentials = false,
    activityMonitor,
    stepUpSeam,
    clientKind = CLIENT_KIND.WEB,
  } = config;

  // An AUTHENTICATED client asks the page's second-factor prompt on a step-up refusal; a public one
  // has nobody to ask.
  const secondFactorSeam = getToken ? stepUpSeam || getPageStepUpSeam() : null;

  // A session client (one that can refresh) judges background requests against the page's
  // user-activity record. Started here, at construction, so an interaction that precedes the first
  // poll is still seen.
  const sessionMonitor = onTokenRefresh ? activityMonitor || getPageActivityMonitor() : null;

  const clientConfig = {
    baseURL,
    timeout,
    // WEB-APP-VERSION-REFRESH-001: every request declares its client — the contract level, and a web page's build —
    // so a server's minimum-contract guard can judge it. Fixed for the life of the page, so set once here.
    headers: { 'Content-Type': 'application/json', ...clientContractHeaders({ kind: clientKind }) },
  };

  if (withCredentials) {
    clientConfig.withCredentials = true;
  }

  const client = axios.create(clientConfig);

  // NO-SESSION-401: `axios.create` copies the GLOBAL defaults, and an app that synced a session token into
  // `axios.defaults.headers.common.Authorization` would hand that token to every client built after a sign-in — a
  // public, platform or storefront client included. A request's credential is attached per request below, never
  // inherited: drop whatever the global held (both spellings; axios headers are case-insensitive only per instance).
  delete client.defaults.headers.common.Authorization;
  delete client.defaults.headers.common.authorization;

  // Request interceptor: attach Bearer token + ABG-001 pre-flight.
  //
  // getToken may return a string (sync — web/localStorage) or a Promise<string>
  // (async — React Native/expo-secure-store). The async interceptor handles
  // both.
  //
  // ABG-001 pre-flight (when getToken is provided): if a request targets a
  // non-public path and no token is available, the request is rejected locally
  // with `NoAuthTokenError` — NO network round-trip. This eliminates the
  // 22-endpoint 401 cascade on stale-token mount documented in
  // `CRITICAL-FIXES/parcelpuffin-uat/dtownretail.dev.parcelpuffin.com-1777343878620.log`.
  //
  // Per-request override:
  //   - `{ authRequired: false }` → bypass pre-flight (treat as public)
  //   - `{ authRequired: true }`  → force pre-flight (override public-path default)
  client.interceptors.request.use(
    async (reqConfig) => {
      // A literal `null` body means "no body" — axios's own idiom for a mutation whose only
      // payload is the URL (`api.post(url, null, { signal })`), and the shape 29 call sites across
      // console, palm-web, payday-web and planner-web use. But this instance FORCES
      // `Content-Type: application/json`, and under a JSON content type axios's transformRequest
      // stringifies `null` too — the wire carried the four bytes `null`, and every API's strict
      // JSON parser refused it with 400 INVALID_JSON BEFORE authentication, so the request never
      // reached a route, a handler, or the request log. Found 2026-09-09 on palm's forced
      // emergency-broadcast acknowledgement, which therefore could never be dismissed. `undefined`
      // already means "no body" to axios; `null` now means the same here, at the ONE seam every
      // web and native client shares.
      if (reqConfig.data === null) {
        reqConfig.data = undefined;
      }

      // Multipart uploads: the instance default forces Content-Type
      // application/json, which strips the multipart boundary and breaks
      // server-side parsing (multer sees no file → 422). Drop the forced header
      // for a FormData body so axios derives `multipart/form-data; boundary=…`.
      // Runs ahead of the no-getToken early return so public upload clients are
      // covered too.
      if (isFormDataBody(reqConfig.data)) {
        stripForcedContentType(reqConfig.headers);
      }

      // BROWSER-TELEMETRY-001 P1: every request the sanctioned transport sends carries W3C
      // trace context (`traceparent`, `x-request-id`, `tracestate` when set) — a child span of the
      // page's trace, so Tempo can parent the API span under the browser's. Injected BEFORE the
      // no-getToken exit below so public clients are traced too; the tracing-browser config
      // (`configureTracing`) decides sampling, not the client.
      reqConfig.headers = injectTraceContext(reqConfig).headers;

      // APPROVALS-STORE-ROOMS-001 U7 (owner ruling R-UAT2-26): every WRITE names the realtime connection it
      // is made from, read from the ONE registered source at request time (a reconnect's new id is what the
      // next write carries; a source answering none stamps nothing). The server's emit for this write comes
      // back stamped with it, and only that connection skips it as its own echo. Ahead of the no-getToken
      // exit, like the trace context, so the ONE rule holds for every client — and composed from the ONE
      // header helper (U10), which the owner-ruled transports outside this package compose too.
      Object.assign(reqConfig.headers, realtimeOriginHeaders(reqConfig.method));

      // No-getToken back-compat: legacy public-only clients run with no
      // pre-flight and no header attachment.
      if (!getToken) {
        return reqConfig;
      }

      // Caller-aborted intent takes precedence over the pre-flight diagnostic.
      // If the user has already canceled, surface AbortError instead of a
      // NoAuthTokenError so consumers can branch on the standard
      // `err.name === 'AbortError'` filter (matches the response-interceptor
      // post-401 abort behavior at line ~167).
      if (reqConfig.signal && reqConfig.signal.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }

      // NO-SESSION-401: whose credential does this request carry? A header equal to the session token this
      // interceptor attached last time is its own (a refresh retry re-sending the config) and takes the current
      // token; ANY other `Authorization` is the caller's (palm-native enrolment's Bearer) and is never replaced.
      // `sessionToken` marks the session token THIS interceptor attached, and nothing else: only a request carrying
      // it may refresh on a 401. A caller cannot pre-set the mark; it is written on every send.
      const presented = reqConfig.headers.Authorization;
      const ownPrevious = reqConfig.sessionToken ? `Bearer ${reqConfig.sessionToken}` : null;
      const callerCredential = presented !== undefined && presented !== null && presented !== ownPrevious;
      const token = callerCredential ? null : await getToken();
      if (token) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      } else if (!callerCredential) {
        delete reqConfig.headers.Authorization;
      }
      reqConfig.sessionToken = token || null;

      // D-1 / D2-Q1: record the token this request presents, and decline a BACKGROUND request whose
      // token was already paused with nobody at the page — no network round-trip, no refresh.
      if (sessionMonitor && token) {
        sessionMonitor.observeToken(token);
        if (reqConfig.background === true && sessionMonitor.isBackgroundPaused(token)) {
          // axios has normalised `method` (lowercase, defaulted to 'get') before any interceptor runs.
          throw new BackgroundRequestPausedError({
            endpoint: reqConfig.url,
            method: reqConfig.method,
            reason: BACKGROUND_PAUSE_REASONS.PAUSED,
          });
        }
      }

      // Resolve auth requirement: per-request override > publicPaths default.
      const explicitOverride = reqConfig.authRequired;
      const isPublic = isPublicPath(reqConfig.url, publicPaths);
      const authRequired =
        explicitOverride === true ? true : explicitOverride === false ? false : !isPublic;

      if (authRequired && !token && !callerCredential) {
        const requestId = (reqConfig.headers && reqConfig.headers['X-Request-ID']) || undefined;
        throw new NoAuthTokenError({
          endpoint: reqConfig.url,
          method: reqConfig.method || 'get',
          requestId,
        });
      }

      return reqConfig;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor: HTML-body guard + 401 refresh + error normalization.
  //
  // AAS-I18N-002 W8 / ruling 23: `assertNotHtmlResponse` sits in the FULFILLED
  // arm because the defect it catches arrives as a **200**. A throw from a
  // fulfilled handler skips this same interceptor's rejected arm (axios chains
  // to the NEXT interceptor's rejected handler), so `HtmlResponseError` reaches
  // consumers typed and unmangled — the same treatment `NoAuthTokenError` gets,
  // and deliberately NOT normalized into the `{status,message,data,code}` shape,
  // which would flatten it into something indistinguishable from a server error.
  client.interceptors.response.use(assertNotHtmlResponse, async (error) => {
    // ABG-001 pre-flight rejections surface as-is so consumers can
    // `instanceof NoAuthTokenError` / `err.code === 'NO_AUTH_TOKEN'`. They
    // are NOT server 401s and must not be normalized to the
    // {status,message,data,code} shape.
    if (error instanceof NoAuthTokenError || error instanceof BackgroundRequestPausedError) {
      return Promise.reject(error);
    }

    // Short-circuit for aborts BEFORE the 401 refresh logic. The
    // consumer contract for abort is a single DOMException with
    // name === 'AbortError' (matches native fetch + the `useAbortableFetch`
    // helper's filter). Axios itself emits CanceledError / ERR_CANCELED
    // / ECONNABORTED in different scenarios; we converge them all here
    // so callers don't have to branch.
    if (isAbortLikeError(error)) {
      return Promise.reject(
        new DOMException(error.message || 'The operation was aborted.', 'AbortError'),
      );
    }

    const originalConfig = error.config;

    // Finish-gate unit M: a same-key request the server is still running (409 IDEMPOTENCY_IN_PROGRESS)
    // waits the server's Retry-After and re-sends the SAME request, within the request's own budget.
    const resent = retryWhileIdempotencyInProgress({ client, error });
    if (resent) return resent;

    // PLN-DEPT-TAB-005: a second-factor refusal prompts (the page's MFA dialog) and retries ONCE.
    if (secondFactorSeam) {
      const retried = retryAfterStepUp({
        client,
        stepUpSeam: secondFactorSeam,
        error,
        reject: (refusal) => Promise.reject(normalizeError(refusal)),
      });
      if (retried) return retried;
    }

    // Attempt token refresh on 401 (only once per request), and ONLY for a request this client sent with the
    // stored session token (NO-SESSION-401): a 401 on a request that carried no session, or the caller's own
    // credential, is that request's refusal — it never refreshes and never ends a session.
    if (
      error.response?.status === 401 &&
      originalConfig &&
      originalConfig.sessionToken &&
      !originalConfig._retryAfterRefresh &&
      onTokenRefresh
    ) {
      // AbortSignal short-circuit: if the caller already aborted before
      // we saw the 401, don't spend a refresh round-trip. Same pattern as
      // the standard DOMException shape fetch() uses, so consumers can
      // check `err.name === 'AbortError'`.
      if (originalConfig.signal?.aborted) {
        return Promise.reject(new DOMException('The operation was aborted.', 'AbortError'));
      }

      // D-1 / D2-Q1: a BACKGROUND request never keeps the session alive on its own. It may refresh
      // only when a person has used the page since this token was first used; otherwise the token
      // is paused for every background request and nothing is refreshed — the session is not over,
      // nobody is here, so `onRefreshFailed` is not told either.
      if (
        originalConfig.background === true &&
        !sessionMonitor.mayRefreshInBackground(originalConfig.sessionToken)
      ) {
        sessionMonitor.pauseBackground(originalConfig.sessionToken);
        return Promise.reject(
          new BackgroundRequestPausedError({
            endpoint: originalConfig.url,
            method: originalConfig.method,
            reason: BACKGROUND_PAUSE_REASONS.IDLE,
          }),
        );
      }

      originalConfig._retryAfterRefresh = true;

      // POS-OFFLINE-STATION-LOCK-001 U15 (owner ruling Q6, "Keep session, all apps"): a refresh the
      // server could not ANSWER is not a refusal. The session is kept — `onRefreshFailed`, which ends
      // it, is not told — and the request rejects as one the server could not be reached for. Any
      // other rejection is a defect and passes through unchanged, never disguised as offline.
      let newToken;
      try {
        newToken = await onTokenRefresh();
      } catch (refreshFailure) {
        if (!isSessionUnreachable(refreshFailure)) throw refreshFailure;
        if (originalConfig.signal?.aborted) {
          return Promise.reject(new DOMException('The operation was aborted.', 'AbortError'));
        }
        return Promise.reject(
          normalizeError({ code: 'ERR_NETWORK', message: refreshFailure.message }),
        );
      }

      // Caller may have aborted while we were awaiting the refresh. Fire
      // no retry; the consumer hook's cleanup has already run, so the
      // retry would land on an unmounted component and the response would
      // be ignored anyway (React 19 no-op) — but the in-flight HTTP work
      // is wasted bandwidth. Reject with AbortError so consumers can
      // filter it the same way they filter native fetch aborts.
      if (originalConfig.signal?.aborted) {
        return Promise.reject(new DOMException('The operation was aborted.', 'AbortError'));
      }

      if (newToken) {
        originalConfig.headers.Authorization = `Bearer ${newToken}`;
        // The retry carries the interceptor's own (new) session token, so its header is recognised as such.
        originalConfig.sessionToken = newToken;
        return client(originalConfig);
      }

      // Refresh failed
      if (onRefreshFailed) {
        onRefreshFailed({ reason: '401_refresh_failed' });
      }
    }

    return Promise.reject(normalizeError(error));
  });

  return client;
}
